import { CheckCircle, XCircle } from 'lucide-react';
import { taskLabel } from './bannerOptions';
import { countdownVerdict, liveActivityVerdict, type PushLogEntry } from './pushLog';

/** One outcome: who, what kind, and everything the server said about it. */
export function PushLogRow({ entry }: { entry: PushLogEntry }) {
  const laV = liveActivityVerdict(entry.liveActivity);
  const cdV = countdownVerdict(entry.countdown);
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      {entry.success ? (
        <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
      ) : (
        <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-gray-900">{entry.empCode}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              entry.type === 'random_attendance'
                ? 'bg-blue-100 text-blue-700'
                : entry.type === 'mood_check'
                ? 'bg-purple-100 text-purple-700'
                : entry.type === 'missed_attendance'
                ? 'bg-teal-100 text-teal-700'
                : entry.type === 'personalized'
                ? 'bg-indigo-100 text-indigo-700'
                : entry.type === 'task'
                ? 'bg-sky-100 text-sky-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {entry.type === 'random_attendance'
              ? `Random Attendance · Slot ${entry.slot} · ${entry.selfieRequired ? 'with selfie' : 'confirm only'}`
              : entry.type === 'mood_check'
              ? 'Mood Check'
              : entry.type === 'missed_attendance'
              ? `Missed Attendance · ${entry.missedSubtype === 'missing-signout' ? 'forgot sign-out' : 'no attendance'}`
              : entry.type === 'personalized'
              ? 'Personalized announcement'
              : entry.type === 'task'
              ? `Task · ${taskLabel(entry.taskType)}`
              : `Celebration · ${entry.celebrationType}`}
          </span>
          <span className="ml-auto flex-shrink-0 text-xs text-gray-400">{entry.timestamp}</span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">{entry.message}</p>
        {entry.content && (
          <div className="mt-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
            <p className="text-xs font-semibold text-gray-800">{entry.content.title}</p>
            <p className="text-xs text-gray-600">{entry.content.body}</p>
          </div>
        )}
        {laV && (
          <p className={`mt-1 text-xs font-medium ${laV.ok ? 'text-green-600' : 'text-amber-700'}`}>{laV.text}</p>
        )}
        {cdV && (
          <p className={`mt-1 text-xs font-medium ${cdV.ok ? 'text-green-600' : 'text-amber-700'}`}>{cdV.text}</p>
        )}
        {entry.diagnostics && (
          <div className="mt-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-gray-600">
            <div>
              <span className="text-gray-400">expo token:</span> {entry.diagnostics.pushTokenPreview || '—'}
              {entry.diagnostics.platform ? ` (${entry.diagnostics.platform})` : ''}
            </div>
            {entry.diagnostics.liveActivity && (
              <div>
                <span className="text-gray-400">LA push-to-start:</span>{' '}
                {entry.diagnostics.liveActivity.tokenPresent ? (
                  <>
                    {entry.diagnostics.liveActivity.tokenPreview}{' '}
                    <span className="text-gray-400">
                      ({entry.diagnostics.liveActivity.tokenBytes} bytes · {entry.diagnostics.liveActivity.tokenSource})
                    </span>
                  </>
                ) : (
                  <span className="text-amber-700">none registered</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Every send and removal since the page was opened, newest first. Kept in this browser tab only. */
export function RecentPushes({ log }: { log: PushLogEntry[] }) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-700">Recent Pushes</h3>
      </div>
      {log.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-400">Nothing sent yet.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {log.map((entry, i) => (
            <PushLogRow key={i} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}
